import { ConfigProvider } from 'antd';
import 'antd/dist/antd.less';
import React, { useEffect, useState, lazy, useRef, useContext } from 'react';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';

import { ThemeProvider } from 'styled-components';
import ProtectedRoute from './components/utilities/protectedRoute';
import config from './config/config';
import store from './redux/store';

import Admin from './routes/admin';
import Auth from './routes/auth';
import Onboarding from './routes/onboarding';
import './static/css/style.css';
import './static/css/newStyle.css';
import { checkPermission } from '../src/utility/utility';
import WebSocketContext from './utility/WebSocketContext';
import actions from './redux/manageMessage/actions';
import { getUnreadMessage } from './redux/manageMessage/actionCreator';
import HttpStatus from './utility/status.enum';
import Intercom from '@intercom/messenger-js-sdk';
import { shutdown } from '@intercom/messenger-js-sdk';
import { getProfile } from './redux/userProfile/actionCreator';
import SetPasswordModal from './container/profile/authentication/SetPasswordModal';
// import ResetPassword from './container/profile/authentication/overview/ResetPassword';

global.backEndPrefix = ``;
global.dateFormat = `MM/dd/yy`;
global.perPageRecord = 30;
global.checkPermission = checkPermission;
global.unsaveMessage = 'You have unsaved changes. Are you sure you want to leave?';
global.isDirtyGlobal = false;
global.adminLoginRoleID = 1;
global.hostLoginRoleID = 3;
global.providerLoginRoleID = 5;
global.isChatStatus = false;  // chat enable or disable status
global.passwordPattern = '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%?&_.])[A-Za-z\\d@$!%?&_.]{8,}$';
global.paymentHostOption = null; // Check status for host payment configuration in Waiver report Tabs (Upcoming / Past Invoices) for host.
global.importFromListingFlag = false;
global.isShowCardsOnFile = false; // Shows or hides the "Card on File" menu option depending on host waiver configuration for host.
global.updatedLoginProfile = null; // default will be null; used for PMS capability where host may change their profile so render to info.js
global.lastActiveTab = 1; // Used for redirect to last active tab
global.hostCreatedDate = null;
global.stripeStatus = null;
global.cardStatus = null;
global.stripeUrl = null;
global.isOAuthFlowTrigger = false; // Used for get "Import from PMS" button without refresh.
global.isAllowClaim = null; //Show or hide "claim" menu
global.isAiChatEnabled = false; //Show or hide "inbox" menu for hosts
global.isScheduledEnabled = false;
global.aiCopilotDurations = [
  { label: 'For 15 minutes', value: 15 },
  { label: 'For 30 minutes', value: 30 },
  { label: 'For 1 hour', value: 60 },
  { label: 'Indefinitely', value: null },
];
global.maxCharCount = 125;
global.mobileBreakPoint = 767;
global.maxHouseRule = 6;
global.isStripeStatus = 0;

global.channelMessage = 1;
global.smsMessage = 2;
global.emailMessage = 3;
global.upsellMarkettingGroupId = 3 // Upsell Marketting Group ID Disable in Messaging
global.aiChatPromptGroupId = 1; // constants.AI_PROMPT_GROUP.AI_CHAT — only this group has Delay Before Response / AI Pause after Escalation
global.listingAutomationPromptGroupId = 2; // constants.AI_PROMPT_GROUP.LISTING_AUTOMATION
global.hostOnboardingPromptGroupId = 4; // constants.AI_PROMPT_GROUP.HOST_ONBOARDING
global.hostTheme6n7 ='XJKY6TLEFWGC';
global.hostThemeCoastal = '0YEBLIQK6CXZ';
global.dollar = '$';

const NotFound = lazy(() => import('./container/pages/404'));

const { themeColor } = config;

function ProviderConfig() {
  const { storeGuestList, storeGuestLastPage, storeSearchText, storeSelectedThread } = actions;
  const { handleWebSocketEvent, setNewMessageReceived } = useContext(WebSocketContext);
  const { backEndPrefix } = global;
  const dispatch = useDispatch();

  const socketRef = useRef(null);
  const { rtl, isLoggedIn, topMenu, mainContent } = useSelector((state) => {
    return {
      rtl: state.ChangeLayoutMode.rtlData,
      topMenu: state.ChangeLayoutMode.topMenu,
      mainContent: state.ChangeLayoutMode.mode,
      isLoggedIn: state.auth.login,
    };
  });

  
  useEffect(() => {
    const checkVersionAndReload = async () => {
      try {
       const response = await fetch(`/version.json?ts=${Date.now()}`, {
        cache: "no-store"
      });
        const data = await response.json();
        const currentVersion = sessionStorage.getItem('app_version');
        if (!currentVersion) {
          sessionStorage.setItem('app_version', data.version);
        } else if (currentVersion !== data.version && !global.isDirtyGlobal) { // isDirtyGlobal true only when user add/edit something
          sessionStorage.setItem('app_version', data.version);
          window.location.reload(true);
        }
      } catch (err) {
        console.error('Failed to fetch app version:', err);
      }
    };

    const intervalId = setInterval(() => {
      checkVersionAndReload();
    }, 60000); // Check every 60 seconds

    return () => clearInterval(intervalId);
  }, []);
  
  const [path, setPath] = useState(window.location.pathname);

  // Post-onboarding password setup: show modal once if host completed signup via phone-OTP
  // (no password set). Flag written by CompleteStep after /complete. Clears on save.
  const [showPasswordModal, setShowPasswordModal] = useState(
    () => localStorage.getItem('onboarding_needs_password') === '1'
  );

  // When onboarding completes without a hard reload, isLoggedIn transitions
  // from falsy → truthy in the same session. Re-check the flag at that point
  // so the modal opens even though the useState initializer already ran.
  useEffect(() => {
    if (isLoggedIn && localStorage.getItem('onboarding_needs_password') === '1') {
      setShowPasswordModal(true);
    }
  }, [isLoggedIn]);

  // True only when the browser is on the OAuth consent path AND carries a valid OAuth param
  // (art = Phase-2 approve/deny, prompt=login = Phase-1 login gate).
  // This lets logged-in users reach the consent screen without touching any other routes.
  const isOAuthConsentFlow =
    path.startsWith('/oauth/consent') &&
    (() => {
      const p = new URLSearchParams(window.location.search);
      return p.has('art') || p.get('prompt') === 'login' || p.has('blocked');
    })();
  useEffect(() => {
    let unmounted = false;
    if (!unmounted) {
      setPath(window.location.pathname);
    }
    return () => {
      unmounted = true;
    };
  }, [setPath]);

  //The useEffect hook is used to update and add the host's creation date and strip status in local storage
  useEffect(() => {
    if (global.hostCreatedDate || global.stripeStatus || global.cardStatus || global.stripeUrl || global.isStripeStatus) {
      let user = JSON.parse(localStorage.getItem('user')) || {};

      // Update ONLY if the key doesn't exist
      if (!user?.host_created_date || user.host_created_date !== global.hostCreatedDate) {
        user.host_created_date = global.hostCreatedDate;
        localStorage.setItem('user', JSON.stringify(user));
      }
      if (!user?.is_stripe_status || user.is_stripe_status !== global.stripeStatus) {
        user.is_stripe_status = global.stripeStatus;
        localStorage.setItem('user', JSON.stringify(user));
      }
      if (!user?.is_card_added || user.is_card_added !== global.cardStatus) {
        user.is_card_added = global.cardStatus;
        localStorage.setItem('user', JSON.stringify(user));
      }
      if (!user?.stripe_invitation_link || user.stripe_invitation_link !== global.stripeUrl) {
        user.stripe_invitation_link = global.stripeUrl;
        localStorage.setItem('user', JSON.stringify(user));
      }
      if (!user?.is_stripe_connect_for_host_claim || user.is_stripe_connect_for_host_claim !== global.isStripeStatus) {
        user.is_stripe_connect_for_host_claim = global.isStripeStatus;
        localStorage.setItem('user', JSON.stringify(user));
      }
    }
  }, [global.hostCreatedDate, global.stripeStatus, global.cardStatus, global.stripeUrl, global.isStripeStatus]);

  useEffect(() => {
    const initializeWebSocket = () => {
      const getUser = JSON.parse(localStorage.getItem('user'));
      // If 'inbox-index' permission has for loggedin user then only the socet will initialize.
      // const isSocketRequired = global.checkPermission("inbox-index");
      const isSocketRequired = !!(
        global.checkPermission("inbox-index") ||
        global.checkPermission("provider-claim-index") ||
        global.checkPermission("payout-report-index")
      );
      
      if (getUser && isSocketRequired) {
        let connectionString =
          '?userId=' +
          getUser?.slug +
          '&userType=' +
          getUser.role_id +
          '&systemId=' +
          localStorage.getItem('system-id');
        let socketUrl = process.env.REACT_APP_WEB_SOCKET_URL + connectionString;

        const ws = new WebSocket(socketUrl);
        socketRef.current = ws;

        const reconnectInterval = 5000; // Reconnect every 5 seconds
        let pingInterval;

        ws.onopen = () => { // WebSocket connection opened
          const timeInterval = 5 * 60 * 1000; // Every 5 minutes
          pingInterval = setInterval(() => { // WebSocket connection: ping-pong
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'ping' }));
            }
          }, timeInterval);
        };

        ws.onmessage = (event) => {
          const parsedMessage = JSON.parse(event?.data);
          if (parsedMessage.message !== 'Forbidden') {
            handleWebSocketEvent(parsedMessage, getUser?.role_id);
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };

        // WebSocket connection closed, attempting to reconnect...
        ws.onclose = () => {
          clearInterval(pingInterval);
          socketRef.current = null; // Reset WebSocket instance

          // Retry connection
          setTimeout(() => {
            if (!socketRef.current && isLoggedIn) {
              // Only attempt to reconnect if user is logged in
              initializeWebSocket();
            }
          }, reconnectInterval);
        };

        return () => {
          clearInterval(pingInterval);
          ws.close();
        };
      }
    };

    if (isLoggedIn && !socketRef.current) {
      initializeWebSocket();
    } else if (!isLoggedIn) {
      // Ensure the WebSocket is closed if the user is not logged in
      if (socketRef.current) {
        socketRef.current.close();
      }
    }
  }, [isLoggedIn, handleWebSocketEvent]);

  const [loginUser, setUserLoginUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });


  useEffect(() => {
    /**
     * Intercom banner/layout integration
     * -------------------------------
     * Intercom Messenger can inject a top "banner" UI that pushes the whole page down (often ~88px)
     * by either:
     *  - adding `margin-top` / `padding-top` on `body`, OR
     *  - inserting a top-of-page fixed element.
     *
     * This app uses many `100vh`-based heights. When Intercom pushes the page down, those `100vh`
     * calculations can create extra scrollbars and/or overlap UI (modals, sticky headers).
     *
     * Global fix:
     *  - Detect whether Intercom is present.
     *  - Measure the injected banner height (best-effort).
     *  - Store it in CSS custom property `--intercom-banner-height` (px).
     *  - Derived CSS var `--app-height` = `100vh - --intercom-banner-height` is used in global CSS.
     *  - Emit a `window` event (`intercom-banner-change`) when the banner height changes so pages with
     *    JS-calculated layouts (e.g. split preview panel) can re-measure.
     */
    const parsePx = (value) => {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    /**
     * Some Intercom variants don't create `#intercom-container`, but do inject iframes.
     * We treat presence of Intercom iframes as "Intercom is active" for measurement purposes.
     */
    const hasIntercomIframes = () =>
      Boolean(document.querySelector('iframe[src*="intercom"], iframe[name*="intercom"]'));

    /**
     * Attempts to calculate the "top offset" introduced by Intercom.
     * Priority:
     *  1) `body` margin/padding top (Intercom sometimes sets these)
     *  2) A top-of-page banner-like element that looks like an Intercom insertion
     *
     * Note: This intentionally remains heuristic so it doesn't depend on fragile Intercom internals.
     */
    const measureIntercomBannerHeight = () => {
      const root = document.getElementById('root');
      const bodyChildren = Array.from(document.body?.children || []);

      // Intercom can push the page down via body margin/padding OR by inserting a top banner element.
      const bodyStyles = window.getComputedStyle(document.body);
      const bodyOffset = Math.max(parsePx(bodyStyles.marginTop), parsePx(bodyStyles.paddingTop));
      // When Intercom pushes the app down without updating `body` styles, `#root` may start below 0.
      // Using this offset avoids false positives from unrelated top-level elements (e.g., AntD message containers).
      const rootOffset = Math.max(0, Math.floor(root?.getBoundingClientRect?.().top || 0));

      let maxBannerHeight = 0;

      for (const el of bodyChildren) {
        if (!el || el === root) continue;

        const tag = el.tagName?.toLowerCase();
        if (tag === 'script' || tag === 'style' || tag === 'link') continue;

        const rect = el.getBoundingClientRect();
        if (rect.height <= 0 || rect.width <= 0) continue;

        const looksLikeTopBanner =
          rect.top <= 2 &&
          rect.left <= 2 &&
          rect.height >= 20 &&
          rect.height <= 200 &&
          rect.width >= window.innerWidth * 0.6;

        if (!looksLikeTopBanner) continue;

        const idClass = `${el.id || ''} ${el.className || ''}`.toLowerCase();
        const attrNames = typeof el.getAttributeNames === 'function' ? el.getAttributeNames() : [];
        const mentionsIntercom =
          idClass.includes('intercom') ||
          attrNames.some((n) => n.toLowerCase().includes('intercom')) ||
          Boolean(el.querySelector?.('iframe[src*="intercom"], iframe[name*="intercom"]')) ||
          (tag === 'iframe' &&
            (idClass.includes('intercom') ||
              (el.src || '').includes('intercom') ||
              (el.name || '').includes('intercom')));

        if (!mentionsIntercom) continue;

        maxBannerHeight = Math.max(maxBannerHeight, rect.height);
      }

      return Math.round(Math.max(bodyOffset, rootOffset, maxBannerHeight));
    };

    let rafId = null;
    let lastBannerHeight = null;

    /**
     * Sets CSS vars + helper body classes:
     *  - `--intercom-banner-height`: numeric px value
     *  - `body.intercom-banner-active`: bannerHeight > 0 (used to offset modals/overlays)
     *  - `body.intercom-active`: legacy class based on `#intercom-container` existence
     */
    const updateIntercomLayoutVars = () => {
      rafId = null;

      const intercomContainer = document.getElementById('intercom-container');
      const hasIntercom = Boolean(intercomContainer) || hasIntercomIframes();
      document.body.classList.toggle('intercom-active', Boolean(intercomContainer));

      if (!hasIntercom) {
        document.documentElement.style.setProperty('--intercom-banner-height', '0px');
        document.body.classList.remove('intercom-banner-active');
        if (lastBannerHeight !== 0) {
          lastBannerHeight = 0;
          // Used by pages that calculate layout in JS (e.g., split preview panel)
          window.dispatchEvent(new CustomEvent('intercom-banner-change', { detail: { height: 0 } }));
        }
        return;
      }

      const bannerHeight = measureIntercomBannerHeight();
      document.documentElement.style.setProperty('--intercom-banner-height', `${bannerHeight}px`);
      document.body.classList.toggle('intercom-banner-active', bannerHeight > 0);
      if (bannerHeight !== lastBannerHeight) {
        lastBannerHeight = bannerHeight;
        // Used by pages that calculate layout in JS (e.g., split preview panel)
        window.dispatchEvent(new CustomEvent('intercom-banner-change', { detail: { height: bannerHeight } }));
      }
    };

    /**
     * Debounce DOM mutation bursts into a single measurement per animation frame.
     * Intercom can mutate DOM multiple times while mounting/unmounting.
     */
    const scheduleUpdate = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(updateIntercomLayoutVars);
    };

    scheduleUpdate();

    /**
     * Route change handling (SPA navigation)
     * -------------------------------------
     * Intercom may already be mounted and not mutate the DOM on route changes.
     * We trigger a recalculation on history navigation so pages that mount/unmount
     * sticky headers / split panels get the correct `--intercom-banner-height` without refresh.
     */
    const onLocationChange = () => scheduleUpdate();
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function pushStatePatched(...args) {
      const result = originalPushState.apply(this, args);
      window.dispatchEvent(new Event('locationchange'));
      return result;
    };

    window.history.replaceState = function replaceStatePatched(...args) {
      const result = originalReplaceState.apply(this, args);
      window.dispatchEvent(new Event('locationchange'));
      return result;
    };

    window.addEventListener('popstate', onLocationChange);
    window.addEventListener('locationchange', onLocationChange);

    // Observe Intercom inserts/removals and recompute. Also recompute on resize.
    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('popstate', onLocationChange);
      window.removeEventListener('locationchange', onLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      if (rafId != null) window.cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const getUser = JSON.parse(localStorage.getItem('user'));      
      const roleTitle = {
        [global.adminLoginRoleID]: "Platform Admin",
        [global.hostLoginRoleID]: "Host",
        [global.providerLoginRoleID]: "Provider"
      };      
      const role = roleTitle[getUser?.role_id] || "Visitor";
      let connectedPMs = getUser?.connected_pms || [];
      if (typeof connectedPMs === "string") {
        if (connectedPMs.trim() === "") {
          connectedPMs = [];
        } else {
          const items = connectedPMs.split(",");
          const result = [];

          for (let i = 0; i < items.length; i++) {
            const trimmed = items[i].trim();
            if (trimmed) result.push(trimmed);
          }
          connectedPMs = result;
        }
      }
      const isStripeConnected = getUser?.is_stripe_status === 2 ? "Yes" : "No";      
      Intercom({
        app_id: process.env.REACT_APP_INTERCOM_APP_ID,
        user_id: getUser?.slug,
        name: getUser?.name,
        email: getUser?.email,
        host_created_at: getUser?.host_created_date,
        is_host_stripe_connected: isStripeConnected,
        is_host_credit_card_added: getUser?.is_card_added,
        stripe_url: getUser?.stripe_invitation_link,
        user_role: role,
        waiver_options: getUser?.waiver_options,
        connected_pms: connectedPMs[0] || null,
        connected_pms_2: connectedPMs[1] || null,
        connected_pms_3: connectedPMs[2] || null,
        connected_pms_4: connectedPMs[3] || null,
        connected_pms_5: connectedPMs[4] || null,
      });
    } else {
      shutdown();
    }
    return () => {
      shutdown();
    };
  }, [isLoggedIn]);

  useEffect(() => {
    const getUser = JSON.parse(localStorage.getItem('user'));
    if (getUser) {
      if(getUser.role_id === global.adminLoginRoleID || getUser.role_id === global.hostLoginRoleID){    
       global.isChatStatus = true;  // setting chat enable status for admin
      }
      setUserLoginUser(getUser);
      // redux cleared for chatApp
      dispatch(storeGuestList(null));
      dispatch(storeGuestLastPage(0));
      dispatch(storeSearchText(''));
      dispatch(storeSelectedThread(null));
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const unreadMessage = async () => {
      try {
        const unreadIndicator = await dispatch(getUnreadMessage());
        if (unreadIndicator.status === HttpStatus.HTTP_OK) {
          if(unreadIndicator?.result?.unread_count > 0){
            setNewMessageReceived(1)
          }
        }
      } catch (error) {}
    };
    if(isLoggedIn && [global.adminLoginRoleID, global.hostLoginRoleID].includes(loginUser?.role_id) && global.checkPermission("inbox-index")){
      unreadMessage();
    }
  }, [isLoggedIn])

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const getUser = JSON.parse(localStorage.getItem('user'));
        if (loginUser) {
          if (loginUser?.role_id !== getUser?.role_id) {
            if ([1, 3].includes(getUser?.role_id)) {
              if (getUser) {
                setUserLoginUser(getUser);
              }
              window.location.href = `${backEndPrefix}/reservation`;
            } else if ([5].includes(getUser?.role_id)) {
              if (getUser) {
                setUserLoginUser(getUser);
              }
              window.location.href = `${backEndPrefix}/waiver-report`;
            } else {
              if (getUser) {
                setUserLoginUser(getUser);
              }
              window.location.reload();
            }
          } else {
            //console.log("User role is the same.");
          }
        } else {
          if (getUser) {
            setUserLoginUser(getUser);
          }
        }
        // If oAuth Flow Trigger and user can comeback to tab and Import from PMS is not visible and Host Login
        if(global.isOAuthFlowTrigger && !global.importFromListingFlag && loginUser?.role_id === global.hostLoginRoleID) {
          const profileResponse = await dispatch(getProfile()); // call profile api for import from PMS sync button on managelisting
            if (profileResponse?.status === HttpStatus.HTTP_OK) {
              global.importFromListingFlag = profileResponse?.result?.is_import_from_pms_enable;
            }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loginUser]);

  useEffect(() => {
    // Run once on component mount
    const handleStorageChange = (event) => {
      // Fires when localStorage changes in another tab
      if (event.key === 'auth_sync') {
        // Check if auth sync key changed
        window.location.reload();
        // Reload tab to update session/profile
      }
    };
    window.addEventListener('storage', handleStorageChange);
    // Listen for storage changes
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      // Cleanup listener on unmount
    };
  }, []); // Empty array = run only once

  return (
    <ConfigProvider direction={rtl ? 'rtl' : 'ltr'}>
      <ThemeProvider theme={{ ...themeColor, rtl, topMenu, mainContent }}>
        <>
          <Router basename={process.env.PUBLIC_URL}>
            {/* re-evaluate if we need a router-level location
                listener instead of reading window.location directly. For Phase 0,
                onboarding is always entered via fresh page load. */}
            {window.location.pathname.startsWith('/onboarding') ? (
              isLoggedIn ? (
                // Completed/admin user landed on an onboarding URL — send them to the
                // Admin app. Do NOT clear any cookie; the Admin session stays alive.
                <Routes>
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              ) : (
                <Routes>
                  <Route path="/onboarding/*" element={<Onboarding />} />
                </Routes>
              )
            ) : !isLoggedIn || isOAuthConsentFlow ? (
              <Routes>
                <Route path="/*" element={<Auth />} />{' '}
              </Routes>
            ) : (
              <Routes>
                <Route path="/*" element={<ProtectedRoute path="/*" Component={Admin} />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            )}
            {isLoggedIn && (path === process.env.PUBLIC_URL || path === `${process.env.PUBLIC_URL}/`) && (
              <Routes>
                <Route path="/" element={<Navigate to="/" />} />
              </Routes>
            )}
            {/* <Routes>
                 <Route path="/reset-passowrd" element={<ResetPassword />} />
            </Routes> */}
            {/* Post-onboarding password setup — shown once for hosts who signed up via phone OTP */}
            {isLoggedIn && showPasswordModal && (
              <SetPasswordModal onClose={() => setShowPasswordModal(false)} />
            )}
          </Router>
        </>
      </ThemeProvider>
    </ConfigProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ProviderConfig />
    </Provider>
  );
}

export default App;
